import React, { useState, useEffect } from 'react';
import { 
  User, 
  BookOpen, 
  Clock, 
  Award, 
  Play, 
  CheckCircle, 
  AlertCircle,
  Calendar,
  Target,
  TrendingUp,
  Star,
  Trophy,
  GraduationCap,
  School,
  Loader,
  LogOut,
  Globe,
  MessageSquare,
  Calculator,
  FileText,
  Monitor,
  Settings,
  Eye,
  EyeOff,
  Save,
  X,
  Mic,
  MessageCircle,
  Volume2,
  VolumeX,
  Download,
  File,
  Video,
  Image
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import alunoPanelService from '../services/alunoPanelService';
import ChatInterface from './ChatInterface';
import { useActivityLogger } from '../hooks/useActivityLogger';
import '../styles/aluno-interface.css';

const AlunoInterface = () => {
  const { user, logout, loading: authLoading } = useAuth();
  
  // Estados principais - declarar aluno primeiro
  const [aluno, setAluno] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'test', 'test-taking', 'results', 'chat'
  const [loading, setLoading] = useState(true);
  
  // Estados para realização do teste
  const [questoesTeste, setQuestoesTeste] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [respostas, setRespostas] = useState({});
  const [tempoInicio, setTempoInicio] = useState(null);
  const [execucaoId, setExecucaoId] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  
  // Hook de logging - agora pode usar aluno?.id
  const { 
    logSessao, 
    logNavegacao, 
    logTeste, 
    logChat, 
    logPerfil, 
    logInteracao, 
    logError,
    startTimer,
    endTimer,
    trackClick,
    isReady
  } = useActivityLogger(aluno?.id);
  const [testesDisponiveis, setTestesDisponiveis] = useState([]);
  const [resultadosHistoricos, setResultadosHistoricos] = useState([]);
  const [estatisticas, setEstatisticas] = useState({
    totalTestes: 0,
    aprovacoes: 0,
    reprovacoes: 0,
    mediaGeral: 0,
    melhorNota: 0,
    ultimoTeste: null
  });
  const [error, setError] = useState(null);
  const [selectedMateria, setSelectedMateria] = useState(null);
  
  // Estados para edição de perfil
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [profileData, setProfileData] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');

  // Estados para teste de áudio
  const [audioTesting, setAudioTesting] = useState(false);
  const [audioConnected, setAudioConnected] = useState(false);
  
  // Estados para teste de conversa
  const [conversationTesting, setConversationTesting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const [peerConnection, setPeerConnection] = useState(null);
  
  // Estados para materiais
  const [materiais, setMateriais] = useState([]);
  const [materiaisLoading, setMateriaisLoading] = useState(false);
  
  // Estados para matérias dinâmicas
  const [materiasList, setMateriasList] = useState([]);
  const [materiasLoading, setMateriasLoading] = useState(false);

  // Debug: Log quando as variáveis mudam
  useEffect(() => {
    console.log('🎯 AlunoInterface estado atualizado:', { 
      timestamp: new Date().toISOString(),
      hasUser: !!user,
      authLoading,
      loading,
      hasAluno: !!aluno,
      error,
      userType: user?.userType,
      userEmail: user?.email
    });
  }, [user, authLoading, loading, aluno, error]);

  // Carregar dados do aluno ao montar o componente
  useEffect(() => {
    // Só carregar dados se o usuário estiver autenticado e não estiver carregando
    if (authLoading || !user) {
      console.log('⏳ Aguardando autenticação...', { authLoading, hasUser: !!user });
      return;
    }

    // Evitar carregamento duplo
    if (aluno && !loading) {
      console.log('✅ Dados já carregados, pulando...');
      return;
    }

    // Log de início de sessão
    if (isReady) {
      logSessao('sessao_inicio', {
        user_email: user.email,
        user_type: user.userType,
        timestamp: new Date().toISOString()
      });
    }

    // Timeout de segurança para evitar carregamento infinito
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.warn('⏰ Timeout de segurança atingido, forçando finalização');
        setLoading(false);
        setError('Timeout de carregamento atingido. Tente recarregar a página.');
        
        // Log do timeout
        if (isReady) {
          logError(new Error('Timeout de carregamento'), {
            contexto: 'carregamento_inicial',
            duracao: 15000
          });
        }
      }
    }, 15000);

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        startTimer('carregamento_dados');
        
        console.log('🔄 Carregando dados do painel...');
        console.log('👤 Usuário autenticado:', user.email);
        console.log('🆔 ID do usuário:', user.id);
        console.log('🎯 Tipo de usuário:', user.userType);
        
        // Buscar dados do aluno
        console.log('🔍 Buscando dados do aluno...');
        const alunoData = await alunoPanelService.getAlunoData(user.email);
        
        if (!alunoData) {
          throw new Error('Dados do aluno não encontrados');
        }
        
        setAluno(alunoData);
        console.log('✅ Dados do aluno carregados:', alunoData.nome);
        
        // Buscar testes disponíveis
        console.log('🔍 Buscando testes disponíveis...');
        const testes = await alunoPanelService.getTestesReais(alunoData.id);
        
        if (!testes) {
          console.warn('⚠️ Nenhum teste encontrado');
          setTestesDisponiveis([]);
        } else {
          setTestesDisponiveis(testes);
          console.log('✅ Testes carregados:', testes.length);
        }
        
        // Buscar resultados históricos
        console.log('🔍 Buscando resultados históricos...');
        const resultados = await alunoPanelService.getResultadosHistoricos(alunoData.id);
        setResultadosHistoricos(resultados);
        console.log('✅ Resultados carregados:', resultados.length);
        
        // Buscar estatísticas
        console.log('🔍 Calculando estatísticas...');
        const stats = await alunoPanelService.getEstatisticasAluno(alunoData.id);
        setEstatisticas(stats);
        console.log('✅ Estatísticas carregadas:', stats);
        
        // Buscar materiais do ano escolar do aluno
        if (alunoData.ano_escolar_id) {
          console.log('🔍 Buscando materiais do ano escolar...');
          setMateriaisLoading(true);
          const materiaisData = await alunoPanelService.getMateriais(alunoData.ano_escolar_id);
          setMateriais(materiaisData);
          setMateriaisLoading(false);
          console.log('✅ Materiais carregados:', materiaisData.length);
        }
        
        // Buscar matérias disponíveis
        console.log('📚 Buscando matérias disponíveis...');
        setMateriasLoading(true);
        const materiasData = await alunoPanelService.getMaterias();
        setMateriasList(materiasData);
        setMateriasLoading(false);
        console.log('✅ Matérias carregadas:', materiasData.length);
        
        // Log de carregamento bem-sucedido
        const loadDuration = endTimer('carregamento_dados');
        if (isReady && alunoData?.id) {
          logNavegacao('acesso_dashboard', {
            aluno_nome: alunoData.nome,
            escola: alunoData.escolas?.nome,
            ano_escolar: alunoData.anos_escolares?.nome,
            testes_disponiveis: testes?.length || 0,
            resultados_historicos: resultados?.length || 0,
            tempo_carregamento: loadDuration
          }, loadDuration);
        }
        
      } catch (error) {
        console.error('❌ Erro ao carregar dados:', error);
        setError(error.message);
        
        // Log do erro
        if (isReady) {
          logError(error, {
            contexto: 'carregamento_dados',
            user_email: user.email
          });
        }
      } finally {
        setLoading(false);
        clearTimeout(timeoutId);
        console.log('🏁 Carregamento finalizado');
      }
    };

    loadData();

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
    };
  }, [user, authLoading, isReady]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'disponivel': return 'status-available';
      case 'em_andamento': return 'status-progress';
      case 'agendado': return 'status-scheduled';
      case 'finalizado': return 'status-completed';
      case 'abandonado': return 'status-abandoned';
      case 'tempo_esgotado': return 'status-timeout';
      case 'aprovado': return 'status-approved';
      case 'reprovado': return 'status-failed';
      case 'pendente': return 'status-pending';
      default: return '';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'disponivel': return 'Disponível';
      case 'em_andamento': return 'Em Andamento';
      case 'agendado': return 'Agendado';
      case 'finalizado': return 'Finalizado';
      case 'abandonado': return 'Abandonado';
      case 'tempo_esgotado': return 'Tempo Esgotado';
      case 'aprovado': return 'Aprovado';
      case 'reprovado': return 'Reprovado';
      case 'pendente': return 'Pendente';
      default: return status;
    }
  };



  const handleStartTest = async (teste) => {
    try {
      setLoading(true);
      startTimer('inicio_teste');
      
      console.log('🚀 Iniciando teste:', {
        testeId: teste.id,
        testeNome: teste.nome,
        alunoId: aluno?.id,
        alunoNome: aluno?.nome
      });
      
      if (!aluno?.id) {
        throw new Error('ID do aluno não encontrado');
      }
      
      // Log do clique no teste
      logTeste('clique_teste', {
        teste_id: teste.id,
        teste_nome: teste.nome,
        materia: teste.materias?.nome,
        duracao_minutos: teste.duracao_minutos,
        questoes: teste.questoes,
        tentativa_numero: (teste.tentativas || 0) + 1
      });
      
      // Iniciar participação no teste
      const result = await alunoPanelService.iniciarTeste(teste.id, aluno.id);
      
      if (!result.success) {
        throw new Error(result.error || 'Erro ao iniciar teste');
      }
      
      console.log('✅ Teste iniciado com sucesso:', result);
      
      const inicioTesteDuration = endTimer('inicio_teste');
      
      // Log de início bem-sucedido
      logTeste('inicio_teste', {
        teste_id: teste.id,
        teste_nome: teste.nome,
        execucao_id: result.execucaoId,
        tempo_para_iniciar: inicioTesteDuration
      }, inicioTesteDuration);
      
      setSelectedTest(teste);
      setExecucaoId(result.execucaoId);
      setCurrentView('test');
      
      // Log de mudança de view
      logNavegacao('mudanca_view', {
        view_anterior: 'dashboard',
        view_atual: 'test',
        teste_id: teste.id
      });
      
    } catch (error) {
      console.error('❌ Erro ao iniciar teste:', error);
      
      // Log do erro
      logError(error, {
        contexto: 'inicio_teste',
        teste_id: teste.id,
        teste_nome: teste.nome
      });
      
      alert(`Erro ao iniciar teste: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBeginTest = async () => {
    try {
      setTestLoading(true);
      
      // Carregar questões do teste
      const questoes = await alunoPanelService.getQuestoesTeste(selectedTest.id);
      
      if (!questoes || questoes.length === 0) {
        throw new Error('Nenhuma questão encontrada para este teste');
      }
      
      console.log('✅ Questões carregadas:', questoes.length);
      setQuestoesTeste(questoes);
      setCurrentQuestionIndex(0);
      setRespostas({});
      setTempoInicio(new Date());
      setCurrentView('test-taking');
    } catch (error) {
      console.error('❌ Erro ao carregar questões:', error);
      alert(`Erro ao carregar questões: ${error.message}`);
    } finally {
      setTestLoading(false);
    }
  };

  const handleAnswerQuestion = (questionId, answer) => {
    const previousAnswer = respostas[questionId];
    
    setRespostas(prev => ({
      ...prev,
      [questionId]: answer
    }));
    
    // Log da resposta
    logTeste('resposta_questao', {
      questao_id: questionId,
      resposta: answer,
      resposta_anterior: previousAnswer,
      questao_numero: currentQuestionIndex + 1,
      tipo_questao: questoesTeste[currentQuestionIndex]?.tipo_questao,
      foi_alteracao: !!previousAnswer && previousAnswer !== answer
    });
    
    // Log específico para mudança de resposta
    if (previousAnswer && previousAnswer !== answer) {
      logTeste('mudanca_resposta', {
        questao_id: questionId,
        resposta_anterior: previousAnswer,
        resposta_nova: answer,
        questao_numero: currentQuestionIndex + 1
      });
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questoesTeste.length - 1) {
      // Log da navegação entre questões
      logTeste('navegacao_questao', {
        questao_atual: currentQuestionIndex + 1,
        questao_destino: currentQuestionIndex + 2,
        direcao: 'proxima',
        respondida: !!respostas[questoesTeste[currentQuestionIndex]?.id]
      });
      
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      // Log da navegação entre questões
      logTeste('navegacao_questao', {
        questao_atual: currentQuestionIndex + 1,
        questao_destino: currentQuestionIndex,
        direcao: 'anterior',
        respondida: !!respostas[questoesTeste[currentQuestionIndex]?.id]
      });
      
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleFinishTest = async () => {
    try {
      setTestLoading(true);
      
      const tempoFim = new Date();
      const tempoGasto = Math.round((tempoFim - tempoInicio) / 60000); // em minutos
      
      const result = await alunoPanelService.finalizarTeste(execucaoId, respostas, tempoGasto);
      
      if (!result.success) {
        throw new Error(result.error || 'Erro ao finalizar teste');
      }
      
      console.log('✅ Teste finalizado com sucesso:', result.resultados);
      
      // Mostrar resultados detalhados
      const { questoesCorretas, totalQuestoes, percentualAcerto, pontuacaoObtida, statusAprovacao } = result.resultados;
      
      const mensagem = `Teste finalizado com sucesso!
      
📊 Seus Resultados:
• Questões corretas: ${questoesCorretas}/${totalQuestoes}
• Percentual de acerto: ${percentualAcerto}%
• Pontuação obtida: ${pontuacaoObtida} pontos
• Status: ${statusAprovacao === 'aprovado' ? '✅ Aprovado' : '❌ Reprovado'}`;
      
      alert(mensagem);
      
      // Voltar ao dashboard e recarregar dados
      setCurrentView('dashboard');
      setSelectedTest(null);
      setQuestoesTeste([]);
      setRespostas({});
      setCurrentQuestionIndex(0);
      setExecucaoId(null);
      
      // Recarregar dados do painel
      window.location.reload();
    } catch (error) {
      console.error('❌ Erro ao finalizar teste:', error);
      alert(`Erro ao finalizar teste: ${error.message}`);
    } finally {
      setTestLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setSelectedTest(null);
    setSelectedMateria(null);
  };

  const handleLogout = () => {
    if (window.confirm('Tem certeza que deseja sair?')) {
      console.log('🚪 Fazendo logout do usuário...');
      
      // Log do logout
      logSessao('logout_aluno', {
        sessao_duracao: Date.now() - new Date(user?.created_at || Date.now()).getTime(),
        timestamp: new Date().toISOString()
      });
      
      logout();
    }
  };

  const handleSubjectClick = (subjectName) => {
    console.log(`🎯 Matéria selecionada: ${subjectName}`);
    
    // Log do clique na matéria
    logNavegacao('clique_materia', {
      materia_nome: subjectName,
      timestamp: new Date().toISOString()
    });
    
    // Abrir chat da matéria selecionada
    setSelectedMateria(subjectName);
    setCurrentView('chat');
    
    // Log da abertura do chat
    logChat('abertura_chat', {
      materia: subjectName,
      view_anterior: 'dashboard'
    });
    
    // Log de mudança de view
    logNavegacao('mudanca_view', {
      view_anterior: 'dashboard',
      view_atual: 'chat',
      materia: subjectName
    });
    
    console.log(`💬 Abrindo chat de ${subjectName}`);
  };

  const handleProfileEdit = () => {
    setShowProfileEdit(true);
    setProfileData({ password: '', confirmPassword: '' });
    setProfileError('');
  };

  const handleCancelProfileEdit = () => {
    setShowProfileEdit(false);
    setProfileData({ password: '', confirmPassword: '' });
    setProfileError('');
  };

  const handleSaveProfile = async () => {
    try {
      setProfileLoading(true);
      setProfileError('');
      
      // Validações
      if (!profileData.password) {
        setProfileError('Nova senha é obrigatória');
        return;
      }
      
      if (profileData.password.length < 6) {
        setProfileError('A senha deve ter pelo menos 6 caracteres');
        return;
      }
      
      if (profileData.password !== profileData.confirmPassword) {
        setProfileError('As senhas não coincidem');
        return;
      }
      
      // Atualizar senha
      const result = await alunoPanelService.updatePassword(aluno.id, profileData.password);
      
      if (!result.success) {
        throw new Error(result.error || 'Erro ao atualizar senha');
      }
      
      alert('Senha atualizada com sucesso!');
      setShowProfileEdit(false);
      setProfileData({ password: '', confirmPassword: '' });
    } catch (error) {
      console.error('❌ Erro ao atualizar perfil:', error);
      setProfileError(error.message);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleAudioTest = async () => {
    try {
      setAudioTesting(true);
      console.log('🎤 Iniciando teste de áudio...');
      
      // Verificar se o navegador suporta getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Seu navegador não suporta captura de áudio');
      }
      
      // Solicitar permissão para microfone
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('✅ Microfone autorizado');
      
      // Testar conexão com servidor de áudio
      try {
        const response = await fetch('https://www.indec-educ.com.br/api/realtime/session', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Servidor de áudio conectado:', data);
          setAudioConnected(true);
          
          // Simular teste de fala
          alert(`🎤 Teste de Áudio Bem-sucedido!\n\n✅ Microfone: Funcionando\n✅ Servidor: Conectado\n\nVocê pode falar com o assistente de IA!`);
        } else {
          throw new Error('Servidor de áudio não disponível');
        }
      } catch (serverError) {
        console.warn('⚠️ Servidor não disponível, testando apenas microfone');
        alert(`🎤 Microfone Testado!\n\n✅ Microfone: Funcionando\n⚠️ Servidor: Offline\n\nSeu microfone está funcionando, mas o servidor de IA não está disponível no momento.`);
      }
      
      // Parar o stream
      stream.getTracks().forEach(track => track.stop());
      
    } catch (error) {
      console.error('❌ Erro no teste de áudio:', error);
      alert(`❌ Erro no Teste de Áudio\n\n${error.message}\n\nVerifique se você permitiu o acesso ao microfone.`);
    } finally {
      setAudioTesting(false);
    }
  };

  const handleConversationTest = async () => {
    if (conversationTesting) {
      // Parar teste de conversa
      stopConversationTest();
      return;
    }

    try {
      setConversationTesting(true);
      console.log('💬 Iniciando teste de conversa com IA...');
      
      // 1. Obter chave efêmera
      const tokenResponse = await fetch('https://www.indec-educ.com.br/api/realtime/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!tokenResponse.ok) {
        throw new Error('Servidor de áudio não disponível');
      }
      
      const tokenData = await tokenResponse.json();
      const EPHEMERAL_KEY = tokenData.client_secret.value;
      console.log('✅ Chave efêmera obtida');
      
      // 2. Criar conexão WebRTC
      const pc = new RTCPeerConnection();
      setPeerConnection(pc);
      
      // 3. Configurar reprodução de áudio remoto
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioEl.controls = false;
      audioEl.volume = 1.0;
      audioEl.muted = false;
      audioEl.style.display = 'none';
      document.body.appendChild(audioEl);
      
      pc.ontrack = e => {
        console.log('🎵 Recebendo track de áudio:', e.track.kind);
        if (e.streams && e.streams[0]) {
          console.log('🔊 Configurando stream de áudio da IA');
          audioEl.srcObject = e.streams[0];
          
          // Forçar reprodução
          audioEl.play().then(() => {
            console.log('✅ Áudio da IA reproduzindo');
          }).catch(err => {
            console.error('❌ Erro ao reproduzir áudio:', err);
          });
        }
      };
      
      // 4. Obter microfone
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(micStream);
      pc.addTrack(micStream.getTracks()[0]);
      console.log('✅ Microfone conectado');
      
      // 5. Configurar data channel
      const dc = pc.createDataChannel('oai-events');
      dc.addEventListener('open', () => {
        console.log('✅ Data channel aberto');
      });
      
      dc.addEventListener('message', (e) => {
        console.log('📨 Mensagem da IA:', e.data);
        try {
          const message = JSON.parse(e.data);
          console.log('🔍 Tipo de mensagem:', message.type);
          
          if (message.type === 'session.created') {
            console.log('✅ Sessão criada com sucesso');
          } else if (message.type === 'session.updated') {
            console.log('✅ Sessão atualizada');
          } else if (message.type === 'response.created') {
            console.log('✅ Resposta criada');
          } else if (message.type === 'response.output_item.added') {
            console.log('✅ Item de resposta adicionado');
          } else if (message.type === 'response.audio.delta') {
            console.log('🔊 Recebendo áudio da IA');
          } else if (message.type === 'response.audio.done') {
            console.log('✅ Áudio da IA finalizado');
          } else if (message.type === 'error') {
            console.error('❌ Erro da OpenAI:', message.error);
          }
        } catch (err) {
          console.log('📨 Mensagem não-JSON:', e.data);
        }
      });
      
      // 6. Criar oferta SDP
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      // 7. Enviar oferta para OpenAI
      const baseUrl = 'https://api.openai.com/v1/realtime';
      const model = 'gpt-4o-realtime-preview-2024-12-17';
      const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${EPHEMERAL_KEY}`,
          'Content-Type': 'application/sdp'
        },
      });
      
      if (!sdpResponse.ok) {
        throw new Error(`Erro na conexão WebRTC: ${sdpResponse.status}`);
      }
      
      // 8. Configurar resposta SDP
      const answer = {
        type: 'answer',
        sdp: await sdpResponse.text(),
      };
      await pc.setRemoteDescription(answer);
      
      console.log('✅ Conexão WebRTC estabelecida');
      setIsRecording(true);
      
      // Aguardar data channel abrir e enviar configuração inicial
      dc.addEventListener('open', () => {
        console.log('📤 Configurando sessão de áudio...');
        
        // Configurar sessão para áudio
        dc.send(JSON.stringify({
          type: 'session.update',
          session: {
            modalities: ['text', 'audio'],
            instructions: 'Você é um assistente de IA. SEMPRE responda em áudio usando sua voz. Seja claro, conciso e fale em português brasileiro.',
            VOICE: 'ash',
            input_audio_format: 'pcm16',
            output_audio_format: 'pcm16',
            input_audio_transcription: {
              model: 'whisper-1'
            },
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: 500
            },
            tools: [],
            tool_choice: 'none',
            temperature: 0.8,
            max_response_output_tokens: 4096
          }
        }));
        
        // Enviar mensagem inicial após configuração
        setTimeout(() => {
          console.log('📤 Enviando item de conversa...');
          dc.send(JSON.stringify({
            type: 'conversation.item.create',
            item: {
              type: 'message',
              role: 'user',
              content: [{
                type: 'input_text',
                text: 'Olá! Este é um teste de conversa. Você pode me responder em áudio, por favor?'
              }]
            }
          }));
          
          // Aguardar um pouco antes de solicitar resposta
          setTimeout(() => {
            console.log('📤 Solicitando resposta em áudio...');
            dc.send(JSON.stringify({
              type: 'response.create',
              response: {
                modalities: ['audio'],
                instructions: 'Responda em áudio em português brasileiro. Diga olá de volta e confirme que você pode ouvir.'
              }
            }));
          }, 200);
          
        }, 1000);
      });

      alert(`🎤 Teste de Conversa Iniciado!\n\n✅ Conexão estabelecida com IA\n🎙️ Fale agora para testar\n\nDiga algo como: "Olá, como você está?"`);
      
    } catch (error) {
      console.error('❌ Erro no teste de conversa:', error);
      alert(`❌ Erro no Teste de Conversa\n\n${error.message}\n\nVerifique se o servidor está rodando.`);
      stopConversationTest();
    }
  };

  const stopConversationTest = () => {
    console.log('🛑 Parando teste de conversa...');
    
    if (audioStream) {
      audioStream.getTracks().forEach(track => track.stop());
      setAudioStream(null);
    }
    
    if (peerConnection) {
      peerConnection.close();
      setPeerConnection(null);
    }
    
    // Remover elemento de áudio
    const audioElements = document.querySelectorAll('audio[autoplay]');
    audioElements.forEach(el => el.remove());
    
    setConversationTesting(false);
    setIsRecording(false);
    
    alert('🛑 Teste de conversa finalizado!');
  };

  // Função para determinar ícone do arquivo
  const getFileIcon = (fileName) => {
    if (!fileName) return <File size={20} />;
    
    const extension = fileName.toLowerCase().split('.').pop();
    
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
      return <Video size={20} />;
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
      return <Image size={20} />;
    }
    return <FileText size={20} />;
  };

  // Função para formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Função para fazer download do material
  const handleDownloadMaterial = async (material) => {
    try {
      console.log('📥 Iniciando download do material:', material.nome);
      
      // Log do download
      logInteracao('download_material', {
        material_id: material.id,
        material_nome: material.nome,
        materia: material.materias?.nome,
        arquivo_tipo: material.arquivo_tipo,
        arquivo_tamanho: material.arquivo_tamanho
      });
      
      // Incrementar contador de download no banco
      const result = await alunoPanelService.incrementarDownload(material.id);
      
      if (result.success) {
        console.log('✅ Contador de download atualizado no banco');
      } else {
        console.warn('⚠️ Erro ao atualizar contador, mas permitindo download');
      }
      
      // Abrir arquivo em nova aba
      window.open(material.arquivo_url, '_blank');
      
      console.log('✅ Download iniciado com sucesso');
    } catch (error) {
      console.error('❌ Erro ao fazer download:', error);
      alert('Erro ao fazer download do material. Tente novamente.');
    }
  };

  // Renderizar tela de teste (instruções)
  if (currentView === 'test' && selectedTest) {
    return (
      <div className="aluno-container">
        <div className="test-interface">
          <div className="test-header">
            <div className="test-info">
              <h1>{selectedTest.nome}</h1>
              <div className="test-meta">
                <span className="meta-item">
                  <BookOpen size={16} />
                  {selectedTest.materias?.nome || 'Matéria não informada'}
                </span>
                <span className="meta-item">
                  <User size={16} />
                  {selectedTest.created_by_name || 'Professor'}
                </span>
                <span className="meta-item">
                  <Clock size={16} />
                  {selectedTest.duracao_minutos || 60} minutos
                </span>
                <span className="meta-item">
                  <Target size={16} />
                  {selectedTest.questoes || 10} questões
                </span>
              </div>
            </div>
            <button className="btn btn-secondary" onClick={handleBackToDashboard}>
              Voltar ao Dashboard
            </button>
          </div>

          <div className="test-content">
            <div className="test-instructions">
              <h3>Instruções</h3>
              <p>{selectedTest.descricao || 'Nenhuma instrução disponível'}</p>
              <div className="test-details">
                <div className="detail-item">
                  <strong>Duração:</strong> {selectedTest.duracao_minutos || 60} minutos
                </div>
                <div className="detail-item">
                  <strong>Questões:</strong> {selectedTest.questoes || 10}
                </div>
                <div className="detail-item">
                  <strong>Pontuação Máxima:</strong> {selectedTest.pontuacao_maxima || 100} pontos
                </div>
                <div className="detail-item">
                  <strong>Tentativas:</strong> {selectedTest.tentativas || 0}/{selectedTest.tentativasMaximas || 3}
                </div>
              </div>
            </div>

            <div className="test-actions">
              <button 
                className="btn btn-primary btn-large" 
                onClick={handleBeginTest}
                disabled={testLoading}
              >
                {testLoading ? (
                  <>
                    <Loader size={20} className="spinning" />
                    Carregando...
                  </>
                ) : (
                  <>
                    <Play size={20} />
                    Iniciar Teste
                  </>
                )}
              </button>
              <p className="test-warning">
                <AlertCircle size={16} />
                Ao iniciar o teste, o cronômetro será ativado e você não poderá pausar.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar tela de realização do teste
  if (currentView === 'test-taking' && questoesTeste.length > 0) {
    const currentQuestion = questoesTeste[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === questoesTeste.length - 1;
    const isFirstQuestion = currentQuestionIndex === 0;
    const answeredQuestions = Object.keys(respostas).length;
    const progress = ((currentQuestionIndex + 1) / questoesTeste.length) * 100;

    return (
      <div className="aluno-container">
        <div className="test-taking-interface">
          {/* Header do teste */}
          <div className="test-taking-header">
            <div className="test-progress">
              <div className="progress-info">
                <h2>{selectedTest.nome}</h2>
                <span>Questão {currentQuestionIndex + 1} de {questoesTeste.length}</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
            <div className="test-stats">
              <span className="stat-item">
                <CheckCircle size={16} />
                {answeredQuestions} respondidas
              </span>
              <span className="stat-item">
                <Clock size={16} />
                {tempoInicio && Math.floor((new Date() - tempoInicio) / 60000)} min
              </span>
            </div>
          </div>

          {/* Conteúdo da questão */}
          <div className="question-content">
            <div className="question-header">
              <h3>Questão {currentQuestionIndex + 1}</h3>
            </div>
            
            <div className="question-text">
              <p>{currentQuestion.questao}</p>
            </div>

            <div className="question-options">
              {currentQuestion.tipo_questao === 'multipla_escolha' && (
                <div className="multiple-choice">
                  {currentQuestion.alternativas && currentQuestion.alternativas.map((alternativa, index) => (
                    <label key={index} className="option-label">
                      <input
                        type="radio"
                        name={`question-${currentQuestion.id}`}
                        value={alternativa}
                        checked={respostas[currentQuestion.id] === alternativa}
                        onChange={(e) => handleAnswerQuestion(currentQuestion.id, e.target.value)}
                      />
                      <span className="option-text">{alternativa}</span>
                    </label>
                  ))}
                </div>
              )}

              {currentQuestion.tipo_questao === 'verdadeiro_falso' && (
                <div className="true-false">
                  <label className="option-label">
                    <input
                      type="radio"
                      name={`question-${currentQuestion.id}`}
                      value="Verdadeiro"
                      checked={respostas[currentQuestion.id] === 'Verdadeiro'}
                      onChange={(e) => handleAnswerQuestion(currentQuestion.id, e.target.value)}
                    />
                    <span className="option-text">Verdadeiro</span>
                  </label>
                  <label className="option-label">
                    <input
                      type="radio"
                      name={`question-${currentQuestion.id}`}
                      value="Falso"
                      checked={respostas[currentQuestion.id] === 'Falso'}
                      onChange={(e) => handleAnswerQuestion(currentQuestion.id, e.target.value)}
                    />
                    <span className="option-text">Falso</span>
                  </label>
                </div>
              )}

              {currentQuestion.tipo_questao === 'dissertativa' && (
                <div className="essay-answer">
                  <textarea
                    placeholder="Digite sua resposta aqui..."
                    value={respostas[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswerQuestion(currentQuestion.id, e.target.value)}
                    rows={6}
                    className="essay-textarea"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Navegação */}
          <div className="question-navigation">
            <div className="nav-buttons">
              <button 
                className="btn btn-secondary"
                onClick={handlePreviousQuestion}
                disabled={isFirstQuestion}
              >
                ← Anterior
              </button>
              
              {!isLastQuestion ? (
                <button 
                  className="btn btn-primary"
                  onClick={handleNextQuestion}
                >
                  Próxima →
                </button>
              ) : (
                <button 
                  className="btn btn-success"
                  onClick={handleFinishTest}
                  disabled={testLoading}
                >
                  {testLoading ? (
                    <>
                      <Loader size={16} className="spinning" />
                      Finalizando...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Finalizar Teste
                    </>
                  )}
                </button>
              )}
            </div>
            
            <div className="question-indicator">
              {questoesTeste.map((_, index) => (
                <button
                  key={index}
                  className={`question-dot ${
                    index === currentQuestionIndex ? 'active' : ''
                  } ${
                    respostas[questoesTeste[index].id] ? 'answered' : ''
                  }`}
                  onClick={() => setCurrentQuestionIndex(index)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar tela de chat
  if (currentView === 'chat' && selectedMateria && aluno) {
    return (
      <ChatInterface
        materia={selectedMateria}
        aluno={aluno}
        onBack={handleBackToDashboard}
        onClose={handleBackToDashboard}
      />
    );
  }

  // Loading state
  if (authLoading) {
    return (
      <div className="aluno-container">
        <div className="loading-container">
          <Loader size={48} className="loading-spinner" />
          <p>Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  // Se não há usuário autenticado
  if (!user) {
    return (
      <div className="aluno-container">
        <div className="error-container">
          <AlertCircle size={48} />
          <h2>Acesso Negado</h2>
          <p>Você precisa estar logado para acessar esta área.</p>
          <button className="btn btn-primary" onClick={() => window.location.href = '/login'}>
            Ir para Login
          </button>
        </div>
      </div>
    );
  }

  // Loading state dos dados
  if (loading) {
    return (
      <div className="aluno-container">
        <div className="loading-container">
          <Loader size={48} className="loading-spinner" />
          <p>Carregando dados do aluno...</p>
        </div>
      </div>
    );
  }

  // Se há erro ou não há dados do aluno
  if (error || !aluno) {
    return (
      <div className="aluno-container">
        <div className="error-container">
          <AlertCircle size={48} />
          <h2>Erro ao carregar dados</h2>
          <p>{error || 'Não foi possível carregar os dados do aluno. Verifique sua conexão.'}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  // Renderizar dashboard principal
  return (
    <div className="aluno-container">
      {/* Header do Aluno - Layout Compacto Horizontal */}
      <div className="aluno-header compact">
        <div className="aluno-logo">
          <img src="/logo.png" alt="INDEC Evolua" className="aluno-logo-image" />
        </div>
        
        <div className="aluno-profile horizontal">
          <div className="profile-avatar compact">
            <img 
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(aluno.nome)}&background=667eea&color=fff&size=60`} 
              alt={aluno.nome} 
            />
          </div>
          <div className="profile-info compact">
            <h1>Olá, {aluno.nome.split(' ')[0]}! 👋</h1>
            <div className="profile-details horizontal">
              <span className="detail-item">
                <School size={14} />
                {aluno.escolas?.nome || 'Escola não informada'}
              </span>
              <span className="detail-item">
                <GraduationCap size={14} />
                {aluno.anos_escolares?.nome || 'Ano não informado'} - {aluno.periodo || 'Período não informado'}
              </span>
              <span className="detail-item">
                <User size={14} />
                RA: {aluno.ra || 'N/A'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="header-actions">
          <button className="profile-edit-button" onClick={handleProfileEdit} title="Editar Perfil">
            <Settings size={18} />
            <span>Perfil</span>
          </button>
          <button className="logout-button" onClick={handleLogout} title="Sair">
            <LogOut size={18} />
            <span>Sair</span>
          </button>
        </div>
      </div>

      {/* Modal de Edição de Perfil */}
      {showProfileEdit && (
        <div className="profile-edit-modal">
          <div className="profile-edit-content">
            <div className="profile-edit-header">
              <h3>Editar Perfil</h3>
              <button className="close-button" onClick={handleCancelProfileEdit}>
                <X size={20} />
              </button>
            </div>
            
            <div className="profile-edit-body">
              {/* Nome (apenas visualização) */}
              <div className="form-group">
                <label>Nome Completo</label>
                <div className="readonly-field">
                  <User size={16} />
                  <span>{aluno.nome}</span>
                </div>
                <small className="field-help">O nome não pode ser alterado pelo aluno</small>
              </div>
              
              {/* Senha */}
              <div className="form-group">
                <label>Nova Senha *</label>
                <div className="password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Digite sua nova senha"
                    value={profileData.password}
                    onChange={(e) => setProfileData(prev => ({ ...prev, password: e.target.value }))}
                    className={profileError && profileError.includes('senha') ? 'error' : ''}
                  />
                  <button 
                    type="button" 
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              
              {/* Confirmar Senha */}
              <div className="form-group">
                <label>Confirmar Nova Senha *</label>
                <div className="password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirme sua nova senha"
                    value={profileData.confirmPassword}
                    onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className={profileError && profileError.includes('coincidem') ? 'error' : ''}
                  />
                </div>
              </div>
              
              {profileError && (
                <div className="profile-error">
                  <AlertCircle size={16} />
                  {profileError}
                </div>
              )}
            </div>
            
            <div className="profile-edit-footer">
              <button 
                className="btn btn-secondary" 
                onClick={handleCancelProfileEdit}
                disabled={profileLoading}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSaveProfile}
                disabled={profileLoading || !profileData.password || !profileData.confirmPassword}
              >
                {profileLoading ? (
                  <>
                    <Loader size={16} className="spinning" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Salvar Alterações
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seção: O que vamos aprender hoje? */}
      <div className="section">
        <div className="section-header">
          <h2>O que vamos aprender hoje?</h2>
          <span className="section-count">
            {materiasLoading ? 'Carregando...' : `${materiasList.length} matérias disponíveis`}
          </span>
        </div>
        
        <div className="subjects-grid">
          {materiasLoading ? (
            <div className="loading-state">
              <Loader size={24} className="spinning" />
              <p>Carregando matérias...</p>
            </div>
          ) : materiasList.length === 0 ? (
            <div className="empty-state">
              <BookOpen size={48} />
              <h3>Nenhuma matéria disponível</h3>
              <p>Não há matérias cadastradas no momento.</p>
            </div>
          ) : (
            materiasList.map(materia => {
              // Mapear ícones baseado no nome da matéria
              const getSubjectIcon = (nome) => {
                const nomeLower = nome.toLowerCase();
                if (nomeLower.includes('cienc')) return <BookOpen size={24} />;
                if (nomeLower.includes('geograf')) return <Globe size={24} />;
                if (nomeLower.includes('histor')) return <Clock size={24} />;
                if (nomeLower.includes('ingl')) return <MessageSquare size={24} />;
                if (nomeLower.includes('matem')) return <Calculator size={24} />;
                if (nomeLower.includes('portug')) return <FileText size={24} />;
                if (nomeLower.includes('tecnolog') || nomeLower.includes('inform')) return <Monitor size={24} />;
                if (nomeLower.includes('fis')) return <Target size={24} />;
                if (nomeLower.includes('quim')) return <Award size={24} />;
                if (nomeLower.includes('biol')) return <Star size={24} />;
                return <BookOpen size={24} />; // Ícone padrão
              };
              
              // Mapear descrições baseado no nome da matéria
              const getSubjectDescription = (nome) => {
                const nomeLower = nome.toLowerCase();
                if (nomeLower.includes('cienc')) return 'Explorar o mundo natural';
                if (nomeLower.includes('geograf')) return 'Conhecer o planeta Terra';
                if (nomeLower.includes('histor')) return 'Descobrir o passado';
                if (nomeLower.includes('ingl')) return 'Aprender um novo idioma';
                if (nomeLower.includes('matem')) return 'Resolver problemas lógicos';
                if (nomeLower.includes('portug')) return 'Dominar nossa língua';
                if (nomeLower.includes('tecnolog') || nomeLower.includes('inform')) return 'Explorar o futuro digital';
                if (nomeLower.includes('fis')) return 'Entender as leis da natureza';
                if (nomeLower.includes('quim')) return 'Descobrir a química da vida';
                if (nomeLower.includes('biol')) return 'Estudar os seres vivos';
                return 'Expandir seus conhecimentos';
              };
              
              return (
                <div key={materia.id} className="subject-card" onClick={() => handleSubjectClick(materia.nome)}>
                  <div className="subject-icon">
                    {getSubjectIcon(materia.nome)}
                  </div>
                  <h3>{materia.nome}</h3>
                  <p>{getSubjectDescription(materia.nome)}</p>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Estatísticas */}
      <div className="stats-section">
        <div className="stats-grid">
          <div className="stat-card primary">
            <div className="stat-icon">
              <Trophy size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{estatisticas.totalTestes}</div>
              <div className="stat-label">Testes Realizados</div>
            </div>
          </div>
          
          <div className="stat-card success">
            <div className="stat-icon">
              <CheckCircle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{estatisticas.aprovacoes}</div>
              <div className="stat-label">Aprovações</div>
            </div>
          </div>
          
          <div className="stat-card warning">
            <div className="stat-icon">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{estatisticas.mediaGeral}</div>
              <div className="stat-label">Média Geral</div>
            </div>
          </div>
          
          <div className="stat-card info">
            <div className="stat-icon">
              <Star size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{estatisticas.melhorNota}</div>
              <div className="stat-label">Melhor Nota</div>
            </div>
          </div>
        </div>
      </div>

      {/* Materiais para Download */}
      <div className="section">
        <div className="section-header">
          <h2>Materiais para Download</h2>
          <span className="section-count">
            {materiaisLoading ? 'Carregando...' : `${materiais.length} materiais`}
          </span>
        </div>
        
        <div className="materials-grid">
          {materiaisLoading ? (
            <div className="loading-state">
              <Loader size={24} className="spinning" />
              <p>Carregando materiais...</p>
            </div>
          ) : materiais.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} />
              <h3>Nenhum material disponível</h3>
              <p>Não há materiais disponíveis para o seu ano escolar no momento.</p>
            </div>
          ) : (
            materiais.map(material => (
              <div key={material.id} className="material-card">
                <div className="material-header">
                  <div className="material-icon">
                    {getFileIcon(material.arquivo_nome)}
                  </div>
                  <div className="material-info">
                    <h4>{material.nome}</h4>
                    <span className="material-subject">
                      {material.materias?.nome || 'Matéria não informada'}
                    </span>
                  </div>
                </div>
                
                <div className="material-details">
                  <div className="detail-item">
                    <span className="detail-label">Arquivo:</span>
                    <span className="detail-value">{material.arquivo_nome || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Tamanho:</span>
                    <span className="detail-value">{formatFileSize(material.arquivo_tamanho)}</span>
                  </div>
                </div>
                
                <div className="material-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleDownloadMaterial(material)}
                    disabled={!material.arquivo_url}
                  >
                    <Download size={16} />
                    Baixar Material
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Testes Disponíveis */}
      <div className="section">
        <div className="section-header">
          <h2>Testes Disponíveis</h2>
          <span className="section-count">{testesDisponiveis.length} testes</span>
        </div>
        
        <div className="tests-grid">
          {testesDisponiveis.length === 0 ? (
            <div className="empty-state">
              <BookOpen size={48} />
              <h3>Nenhum teste disponível</h3>
              <p>Não há testes agendados para você no momento.</p>
            </div>
          ) : (
            testesDisponiveis.map(teste => {
              const materiaColor = teste.materias?.nome === 'Matemática' ? '#3b82f6' :
                                 teste.materias?.nome === 'História' ? '#10b981' :
                                 teste.materias?.nome === 'Português' ? '#f59e0b' :
                                 teste.materias?.nome === 'Física' ? '#8b5cf6' :
                                 teste.materias?.nome === 'Química' ? '#ef4444' :
                                 '#64748b';

              return (
                <div key={teste.id} className="test-card">
                  <div className="test-card-header" style={{ borderLeftColor: materiaColor }}>
                    <div className="test-title">
                      <h3>{teste.nome}</h3>
                      <span className={`test-status ${getStatusColor(teste.status)}`}>
                        {getStatusText(teste.status)}
                      </span>
                    </div>
                    <div className="test-subject" style={{ color: materiaColor }}>
                      {teste.materias?.nome || 'Matéria não informada'}
                    </div>
                  </div>
                  
                  <div className="test-card-body">
                    <div className="test-meta-grid">
                      <div className="meta-item">
                        <User size={16} />
                        <span>{teste.created_by_name || 'Professor'}</span>
                      </div>
                      <div className="meta-item">
                        <Calendar size={16} />
                        <span>{formatDate(teste.created_at)}</span>
                      </div>
                      <div className="meta-item">
                        <Clock size={16} />
                        <span>{teste.duracao_minutos || 60} min</span>
                      </div>
                      <div className="meta-item">
                        <Target size={16} />
                        <span>{teste.questoes || 10} questões</span>
                      </div>
                    </div>
                    
                    {teste.ultimaExecucao && (
                      <div className="test-execution-info">
                        <strong>Última tentativa:</strong> {formatDate(teste.ultimaExecucao.data_inicio)}
                        {teste.ultimaExecucao.status_execucao === 'finalizado' && (
                          <span className="execution-score">
                            Pontuação: {teste.ultimaExecucao.pontuacao_obtida || 0}
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="test-attempts">
                      <strong>Tentativas:</strong> {teste.tentativas || 0}/{teste.tentativasMaximas || 3}
                    </div>

                    {teste.descricao && (
                      <div className="test-instructions-preview">
                        <strong>Descrição:</strong> {teste.descricao.substring(0, 100)}...
                      </div>
                    )}
                  </div>
                  
                  <div className="test-card-footer">
                    <button 
                      className={`btn ${teste.podeIniciar ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => handleStartTest(teste)}
                      disabled={!teste.podeIniciar || loading}
                    >
                      {loading && <Loader size={16} className="spinning" />}
                      {!loading && teste.status === 'em_andamento' && <Play size={16} />}
                      {!loading && teste.status === 'disponivel' && <Play size={16} />}
                      {!loading && teste.status === 'finalizado' && <CheckCircle size={16} />}
                      
                      {loading && 'Carregando...'}
                      {!loading && teste.status === 'em_andamento' && 'Continuar'}
                      {!loading && teste.status === 'disponivel' && 'Iniciar Teste'}
                      {!loading && teste.status === 'finalizado' && 'Finalizado'}
                      {!loading && !teste.podeIniciar && 'Não disponível'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Resultados Recentes */}
      <div className="section">
        <div className="section-header">
          <h2>Resultados Recentes</h2>
          <span className="section-count">{resultadosHistoricos.length} resultados</span>
        </div>
        
        <div className="results-grid">
          {resultadosHistoricos.length === 0 ? (
            <div className="empty-state">
              <Award size={48} />
              <h3>Nenhum resultado ainda</h3>
              <p>Seus resultados de testes aparecerão aqui após a conclusão.</p>
            </div>
          ) : (
            resultadosHistoricos.map(resultado => (
              <div key={resultado.id} className="result-card">
                <div className="result-header">
                  <div className="result-title">
                    <h4>{resultado.nomeTest}</h4>
                    <span className="result-subject">{resultado.materia}</span>
                  </div>
                  <span className={`result-status ${getStatusColor(resultado.status)}`}>
                    {getStatusText(resultado.status)}
                  </span>
                </div>
                
                <div className="result-body">
                  <div className="result-score">
                    <div className="score-main">
                      <span className="score-value">{resultado.pontuacao}</span>
                      <span className="score-max">/{resultado.pontuacaoMaxima}</span>
                    </div>
                    <div className="score-percentage">
                      {resultado.percentual}%
                    </div>
                  </div>
                  
                  <div className="result-details">
                    <div className="detail-row">
                      <span>Data:</span>
                      <span>{formatDate(resultado.dataRealizacao)}</span>
                    </div>
                    <div className="detail-row">
                      <span>Tempo:</span>
                      <span>{resultado.tempo} min</span>
                    </div>
                    <div className="detail-row">
                      <span>Acertos:</span>
                      <span>{resultado.questoesCorretas}/{resultado.questoesTotal}</span>
                    </div>
                    {resultado.tentativa > 1 && (
                      <div className="detail-row">
                        <span>Tentativa:</span>
                        <span>{resultado.tentativa}ª</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AlunoInterface;
